import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import { MarkDownText } from '../Text'
import Icon from '../icon/Icon'
import TabsWithContent from '../TabsWithContent'
import { RowAligned } from '../layout/LayoutAtoms'
import Fold from '../Fold'
import { CompetencyDetails, StyledIconContainer } from '../CompetencyChip'

// Utils
import { useAppState } from '../../context/AppContext'
import { cultureValueColors } from '../../utils/habitHelper'
import { getCultureTabs } from '../../common/helpers/company.helper'

// Styles
import { hsla, hslToArray } from '../../styles/colors'
import { roundedCardStyles } from './GenericCard'
import { shadows } from '../../styles/effects'
import { mqFrom } from '../../styles/mediaQueries'

// Types
import {
  ICompanyCultureValue,
  ICompanyJobLevel,
} from '../../common/models/Company'

interface IProps {
  tabsOpened?: boolean
  resultTab?: React.ReactNode
  descriptionFoldable?: boolean
  headerTags?: React.ReactNode
  complete?: boolean
  children?: React.ReactNode
  className?: string
  value: ICompanyCultureValue
  memberLevel?: ICompanyJobLevel
}

const ValueCard: React.FC<IProps> = ({
  tabsOpened,
  headerTags,
  resultTab,
  descriptionFoldable,
  complete,
  children,
  className,
  value,
  memberLevel,
}) => {
  const { t } = useTranslation()
  const { theme, company } = useAppState()
  const { lightValueColor, darkValueColor, textColor } = cultureValueColors(
    theme,
    value.color,
    company,
    !company && hslToArray(value.color)
  )

  const tabs = getCultureTabs(value, memberLevel)

  return (
    <Container className={className}>
      <Body backgroundColor={lightValueColor}>
        <RowAligned margin="0 0 16px">
          <StyledCompetencyDetails
            name={value.name}
            subtitle={value.subtitle}
            titleColor={darkValueColor}
            color={value.color as any}
            subtitleColor={theme.colors[value.color]}
            focus
            iconProps={{
              size: 16,
              type: 'Culture',
              color: theme.colors.lightest,
            }}
          />
          {(complete || headerTags) && (
            <RowAligned margin="0 0 0 auto">
              {complete ? (
                <Icon type="Check" color={textColor} size={14} />
              ) : headerTags ? (
                headerTags
              ) : undefined}
            </RowAligned>
          )}
        </RowAligned>
        {value.description &&
          (descriptionFoldable ? (
            <Fold
              caption={t('valueCard.description')}
              color={darkValueColor}
              hoverColor={theme.colors[value.color]}
              content={
                <Markdown
                  textStyle="bodySmall"
                  textColor={darkValueColor}
                  marginBottom={8}
                >
                  {value.description}
                </Markdown>
              }
            />
          ) : (
            <>
              <Markdown
                textStyle="bodySmall"
                textColor={darkValueColor}
                marginBottom={8}
              >
                {value.description}
              </Markdown>
            </>
          ))}
        {tabs && tabs.length > 0 && (
          <Fold
            caption={t('general.examples')}
            opened={tabsOpened}
            color={darkValueColor}
            hoverColor={theme.colors[value.color]}
            content={
              <StyledTabsWithContent
                textColor={darkValueColor}
                tabColor={theme.colors[value.color]}
                defaultTabColor={lightValueColor}
                tabs={tabs}
                updateTab={(_, e) => {
                  e.stopPropagation()
                }}
              />
            }
          />
        )}
        {resultTab && (
          <Fold
            caption={t('evaluationModal.titles.termEvaluationResults')}
            opened
            color={darkValueColor}
            hoverColor={theme.colors[value.color]}
            content={resultTab}
          />
        )}
      </Body>
      {children && children}
    </Container>
  )
}

export default ValueCard

const Container = styled.div`
  ${roundedCardStyles};
  position: relative;
  overflow: hidden;
  box-shadow: ${shadows.depthTwo};
`

const Body = styled.div<{ backgroundColor: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  padding: 16px;

  ${mqFrom.M`
    padding: 24px;
  `}
`

const StyledCompetencyDetails = styled(CompetencyDetails)`
  ${StyledIconContainer} {
    border-radius: 50%;
  }
`

const Markdown = styled(MarkDownText)<{ textColor: string; fade?: boolean }>`
  color: ${({ textColor, fade }) => hsla(textColor, fade ? 0.65 : 1)};
`

const StyledTabsWithContent = styled(TabsWithContent)`
  background-color: ${({ theme }) => theme.colors.lightest};
`
